import { ApiError } from '../../services/api';
import type { PackagePricingMode, PackageRequest, PackageResponse, PackageStatus } from '../../types/packages';

/**
 * Spec 081b — admin edits a package in place (status kept, `PACKAGE_ADMIN_EDITED`). Scope mirrors
 * `OfferEditPage`: texts, cover, price or percent, window. Items stay as the garage built them
 * (sent back unchanged, since `PackageRequest` needs them). The server stays authoritative.
 */

export const NAME_MAX = 60;
export const DESCRIPTION_MAX = 1000;
export const PERCENT_MIN = 5;
export const PERCENT_MAX = 70;
export const MAX_WINDOW_DAYS = 90;

/** A running package keeps its start date. */
export const START_LOCKED_STATUSES: readonly PackageStatus[] = ['LIVE', 'PAUSED'];

export const PACKAGE_STATE_CHANGED_EDIT_MESSAGE = 'This package changed, reload and try again';

export interface PackageEditForm {
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  coverStorageKey: string | null;
  coverUrl: string | null;
  packagePrice: string;
  discountPercent: string;
  startsAt: string;
  endsAt: string;
}

export type PackageEditField = Exclude<keyof PackageEditForm, 'coverUrl'> | 'items';
export type PackageEditErrors = Partial<Record<PackageEditField, string>>;

const numStr = (n: number | null | undefined) => (n == null ? '' : String(n));
const optText = (s: string) => (s.trim() ? s.trim() : null);
const optNum = (s: string) => (s.trim() === '' ? null : Number(s));

export function formFromPackage(p: PackageResponse): PackageEditForm {
  return {
    nameEn: p.nameEn ?? '',
    nameAr: p.nameAr ?? '',
    descriptionEn: p.descriptionEn ?? '',
    descriptionAr: p.descriptionAr ?? '',
    coverStorageKey: p.cover?.storageKey ?? null,
    coverUrl: p.cover?.downloadUrl ?? null,
    packagePrice: numStr(p.packagePrice),
    discountPercent: numStr(p.discountPercent),
    startsAt: (p.startsAt ?? '').slice(0, 10),
    endsAt: (p.endsAt ?? '').slice(0, 10),
  };
}

export function formToPackageRequest(f: PackageEditForm, p: PackageResponse): PackageRequest {
  const fixed = p.pricingMode === 'FIXED';
  return {
    nameEn: f.nameEn.trim(),
    nameAr: optText(f.nameAr),
    descriptionEn: optText(f.descriptionEn),
    descriptionAr: optText(f.descriptionAr),
    coverStorageKey: f.coverStorageKey,
    items: p.items.map((i) => ({ offeringId: i.offeringId, fixedVariationValueIds: i.fixedChoices.map((c) => c.valueId) })),
    packagePrice: fixed ? optNum(f.packagePrice) : null,
    discountPercent: fixed ? null : optNum(f.discountPercent),
    startsAt: f.startsAt || null,
    endsAt: f.endsAt || null,
    version: p.version,
  };
}

/** Labels of the fields that differ from the loaded package, in form order. */
export function changedPackageFields(o: PackageEditForm, f: PackageEditForm, mode: PackagePricingMode): string[] {
  const out: string[] = [];
  const text = (a: string, b: string) => a.trim() !== b.trim();
  if (text(o.nameEn, f.nameEn)) out.push('Name (English)');
  if (text(o.nameAr, f.nameAr)) out.push('Name (Arabic)');
  if (text(o.descriptionEn, f.descriptionEn)) out.push('Description (English)');
  if (text(o.descriptionAr, f.descriptionAr)) out.push('Description (Arabic)');
  if (o.coverStorageKey !== f.coverStorageKey) out.push('Cover');
  if (mode === 'FIXED' && optNum(o.packagePrice) !== optNum(f.packagePrice)) out.push('Package price');
  if (mode === 'PERCENT' && optNum(o.discountPercent) !== optNum(f.discountPercent)) out.push('Discount');
  if (o.startsAt !== f.startsAt) out.push('Start date');
  if (o.endsAt !== f.endsAt) out.push('End date');
  return out;
}

function daysBetween(fromIso: string, toIso: string): number {
  const ms = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, (m || 1) - 1, d || 1);
  };
  return Math.round((ms(toIso) - ms(fromIso)) / 86400000);
}

export function validatePackageEdit(
  f: PackageEditForm,
  ctx: { mode: PackagePricingMode; originalTotal: number | null; originalStartsAt: string; today: string },
): PackageEditErrors {
  const e: PackageEditErrors = {};
  if (!f.nameEn.trim()) e.nameEn = 'Enter an English name.';
  else if (f.nameEn.trim().length > NAME_MAX) e.nameEn = `At most ${NAME_MAX} characters.`;
  if (f.nameAr.trim().length > NAME_MAX) e.nameAr = `At most ${NAME_MAX} characters.`;
  if (f.descriptionEn.trim().length > DESCRIPTION_MAX) e.descriptionEn = `At most ${DESCRIPTION_MAX} characters.`;
  if (f.descriptionAr.trim().length > DESCRIPTION_MAX) e.descriptionAr = `At most ${DESCRIPTION_MAX} characters.`;
  if (ctx.mode === 'FIXED') {
    const v = Number(f.packagePrice);
    if (f.packagePrice.trim() === '' || !Number.isFinite(v) || v <= 0) e.packagePrice = 'Enter the package price.';
    else if (!/^\d+(\.\d{1,2})?$/.test(f.packagePrice.trim())) e.packagePrice = 'Use at most 2 decimals.';
    else if (ctx.originalTotal != null && v >= ctx.originalTotal) {
      e.packagePrice = `The package price must be below the original total (AED ${ctx.originalTotal}).`;
    }
  } else {
    const v = Number(f.discountPercent);
    if (f.discountPercent.trim() === '' || !Number.isInteger(v) || v < PERCENT_MIN || v > PERCENT_MAX) {
      e.discountPercent = `Enter a whole percentage from ${PERCENT_MIN} to ${PERCENT_MAX}.`;
    }
  }
  if (f.startsAt && f.startsAt !== ctx.originalStartsAt && f.startsAt < ctx.today) e.startsAt = 'The package cannot start before today.';
  if (f.endsAt) {
    if (f.endsAt < ctx.today) e.endsAt = 'The end date must be today or later.';
    else if (f.startsAt && f.endsAt < f.startsAt) e.endsAt = 'The end date must be on or after the start date.';
    else if (f.startsAt && daysBetween(f.startsAt, f.endsAt) > MAX_WINDOW_DAYS) e.endsAt = `A package can run for up to ${MAX_WINDOW_DAYS} days.`;
  }
  return e;
}

export interface PackageEditErrorView {
  fieldErrors: PackageEditErrors;
  formError: string | null;
  /** Show a Reload button (stale version). */
  reload: boolean;
  /** The package can no longer be edited (ENDED). */
  blocked: boolean;
}

const CODE_FIELD: Record<string, PackageEditField> = {
  PACKAGE_PRICE_NOT_BELOW_TOTAL: 'packagePrice',
  PACKAGE_PERCENT_RANGE: 'discountPercent',
  PACKAGE_WINDOW_INVALID: 'endsAt',
  PACKAGE_ITEMS_COUNT: 'items',
  PACKAGE_ITEM_INELIGIBLE: 'items',
  CONTACT_DETAILS_IN_TEXT: 'descriptionEn',
  INVALID_STORAGE_KEY: 'coverStorageKey',
};

const SERVER_FIELDS: readonly PackageEditField[] = [
  'nameEn', 'nameAr', 'descriptionEn', 'descriptionAr', 'coverStorageKey', 'packagePrice', 'discountPercent', 'startsAt', 'endsAt', 'items',
];

export function mapPackageEditError(err: unknown): PackageEditErrorView {
  const out: PackageEditErrorView = { fieldErrors: {}, formError: null, reload: false, blocked: false };
  if (!(err instanceof ApiError)) {
    out.formError = err instanceof Error && err.message ? err.message : 'Could not save the package.';
    return out;
  }
  const message = err.serverMessage || err.message;
  if (err.code === 'PACKAGE_STATE_CHANGED') return { ...out, formError: PACKAGE_STATE_CHANGED_EDIT_MESSAGE, reload: true };
  if (err.code === 'PACKAGE_INVALID_TRANSITION') return { ...out, formError: 'This package has ended and can no longer be edited.', blocked: true };
  if (err.status === 403) return { ...out, formError: 'You do not have access to edit packages.' };
  if (err.status === 404) return { ...out, formError: 'This package no longer exists.' };
  for (const [field, code] of Object.entries(err.errors ?? {})) {
    const key = (SERVER_FIELDS as readonly string[]).includes(field) ? (field as PackageEditField) : CODE_FIELD[code];
    if (key) out.fieldErrors[key] = message || code;
  }
  const codeField = err.code ? CODE_FIELD[err.code] : undefined;
  if (codeField && !out.fieldErrors[codeField]) out.fieldErrors[codeField] = message;
  out.formError = Object.keys(out.fieldErrors).length ? 'Fix the highlighted fields.' : message || 'Could not save the package.';
  return out;
}
